import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet } from "react-router-dom";
import { api } from "../lib/api";
import { useSession } from "../lib/session";

/**
 * Settings, with the sections down the side.
 *
 * Only sections with something real in them. A settings screen full of controls
 * that do nothing is worse than a short one, because it takes a person two
 * visits to learn which half is furniture.
 */
/** react-router does not add a class for the current link on its own. */
function active({ isActive }: { isActive: boolean }): string {
  return isActive ? "active" : "";
}

export function SettingsRoute() {
  return (
    <section className="settings">
      <nav className="settings-nav" aria-label="Settings">
        <NavLink to="/settings" end className={active}>
          Account
        </NavLink>
        <NavLink to="/settings/keys" className={active}>
          Agent keys
        </NavLink>
        <NavLink to="/settings/connectors" className={active}>
          Connectors
        </NavLink>
        <NavLink to="/settings/sessions" className={active}>
          Sessions
        </NavLink>
        <NavLink to="/settings/about" className={active}>
          This deployment
        </NavLink>
      </nav>
      <div className="settings-body">
        <Outlet />
      </div>
    </section>
  );
}

/**
 * Who you are signed in as.
 *
 * Nothing here is editable, and saying so is the point: identity comes from
 * Google and is not scenix's to change (ADR 000006). A name field that silently
 * reverts on next sign-in would be worse than none.
 */
export function AccountSettings() {
  const { session, signOut } = useSession();

  // The shell only renders this when signed in, so this is a type narrowing
  // rather than a case that happens.
  if (session.state !== "signed-in") return null;
  const { user } = session;

  return (
    <>
      <h1>Account</h1>
      <p className="muted">
        You sign in with Google, so your name and picture come from there and change there (ADR
        000006).
      </p>

      <dl className="facts">
        <dt>Name</dt>
        <dd>{user.displayName}</dd>
        <dt>Email</dt>
        <dd>{user.email}</dd>
        <dt>User</dt>
        <dd>
          <code>{user.id}</code>
        </dd>
      </dl>

      <h2 className="section-head">Spaces</h2>
      <SpaceMemberships />

      <p className="danger-zone">
        <button type="button" className="ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </p>
    </>
  );
}

/** Where this account can see anything, and at what role. */
function SpaceMemberships() {
  const spaces = useQuery({ queryKey: ["spaces"], queryFn: () => api.listSpaces() });

  if (spaces.isPending) return <p role="status">Loading…</p>;
  if (spaces.error) return <p className="muted">Could not load your spaces.</p>;
  if (spaces.data.length === 0) return <p className="muted">You are not in any space yet.</p>;

  return (
    <dl className="facts">
      {spaces.data.map((space) => (
        <div key={space.id} className="fact-row">
          <dt>{space.name}</dt>
          <dd>{space.role}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What this instance is, which is the question worth answering when something
 * is wrong: which build, talking to what, and who it lets in.
 */
export function AboutSettings() {
  const health = useQuery({ queryKey: ["health"], queryFn: () => api.health() });

  return (
    <>
      <h1>This deployment</h1>
      <p className="muted">
        What you are looking at right now. Useful when something behaves unexpectedly and the first
        question is which build you are on.
      </p>

      <dl className="facts">
        <dt>Version</dt>
        <dd>
          <code>{health.data?.version ?? "…"}</code>
        </dd>
        <dt>API</dt>
        <dd>
          <code>{window.location.origin}/v1</code>
        </dd>
        <dt>Status</dt>
        <dd>{health.isError ? "unreachable" : (health.data?.status ?? "…")}</dd>
      </dl>

      <h2 className="section-head">Who can sign in</h2>
      <p className="muted">
        Set on the server, not here. A deployed scenix refuses to start without an answer, so this
        is never accidentally open to everybody (ADR 000031). Changing it is a deploy.
      </p>
    </>
  );
}
